import { Color, drawing as d, geometry as g } from '@progress/kendo-drawing';
import { isString } from './../utils';
import { TRANSPARENT } from '../constants';
/**
 * Gets size options from configuration or returns default values.
 * @param options - The options object to extract size values from
 * @returns An object containing x, y, width, and height values
 */
export function sizeOptionsOrDefault(options) {
    return {
        x: options.x || 0,
        y: options.y || 0,
        width: options.width || 0,
        height: options.height || 0
    };
}
/**
 * Normalizes drawing options to ensure consistent format.
 * @param options - The drawing options to normalize
 * @returns Normalized drawing options with proper color format
 */
export function normalizeDrawingOptions(options) {
    if (options) {
        let drawingOptions = options;
        if (isString(drawingOptions)) {
            drawingOptions = {
                color: drawingOptions
            };
        }
        if (drawingOptions.color) {
            drawingOptions.color = getColor(drawingOptions.color);
        }
        return drawingOptions;
    }
}
/**
 * Converts a color value to a proper hex color format.
 * @param value - The color value to convert
 * @returns The converted color in hex format or the original value for transparent
 */
export function getColor(value) {
    let color;
    if (value !== TRANSPARENT) {
        color = new Color(value).toHex();
    }
    else {
        color = value;
    }
    return color;
}
/**
 * Calculates the angle of a line between two points.
 * @param p1 - The first point
 * @param p2 - The second point
 * @returns The angle in degrees between the two points
 */
export function lineAngle(p1, p2) {
    const xDiff = p2.x - p1.x;
    const yDiff = p2.y - p1.y;
    const angle = d.util.deg(Math.atan2(yDiff, xDiff));
    return angle;
}
/**
 * Creates a path segment from x and y coordinates.
 * @param x - The x coordinate
 * @param y - The y coordinate
 * @returns A new path segment
 */
export function createSegment(x, y) {
    return new g.Segment(new g.Point(x, y));
}
/**
 * Converts a rectangle to a drawing rectangle format.
 * @param rect - The rectangle to convert
 * @returns A drawing rectangle or undefined if rect is not provided
 */
export function toDrawingRect(rect) {
    if (rect) {
        return new g.Rect([rect.x, rect.y], [rect.width, rect.height]);
    }
}
/**
 * Removes all child elements from a DOM element.
 * @param element - The DOM element to remove children from
 */
export function removeChildren(element) {
    while (element.firstChild) {
        element.removeChild(element.firstChild);
    }
}
/**
 * Gets spacing values from a number or spacing object.
 * @param value - The spacing value as number or object with side-specific values
 * @param defaultSpacing - Default spacing value to use for undefined sides
 * @returns A complete spacing object with values for all four sides
 */
export function getSpacing(value, defaultSpacing = 0) {
    const spacing = { top: 0, right: 0, bottom: 0, left: 0 };
    if (typeof value === 'number') {
        spacing.top = spacing.right = spacing.bottom = spacing.left = value;
    }
    else {
        spacing.top = value.top || defaultSpacing;
        spacing.right = value.right || defaultSpacing;
        spacing.bottom = value.bottom || defaultSpacing;
        spacing.left = value.left || defaultSpacing;
    }
    return spacing;
}
/**
 * Draws a polygon with rounded corners.
 * @param path - The path or multipath to draw on
 * @param points - Array of points defining the polygon vertices
 * @param r - Corner radius value or array of radius values per vertex
 */
export const drawRoundedPolygon = (path, points, r) => {
    const n = points.length;
    for (let i = 0; i < n; i++) {
        const prev = points[(i + n - 1) % n];
        const curr = points[i];
        const next = points[(i + 1) % n];
        const cornerRadius = Array.isArray(r) ? r[i] : r;
        // --- vectors from the vertex to the neighbors ---
        const v1 = { x: prev.x - curr.x, y: prev.y - curr.y };
        const v2 = { x: next.x - curr.x, y: next.y - curr.y };
        const len1 = Math.hypot(v1.x, v1.y);
        const len2 = Math.hypot(v2.x, v2.y);
        // --- internal angle φ (phi) ---
        const cosPhi = (v1.x * v2.x + v1.y * v2.y) / (len1 * len2);
        const phi = Math.acos(Math.max(-1, Math.min(1, cosPhi))); // clamp for safety
        // --- distance Δs = r / tan(φ/2) ---
        const ds = cornerRadius / Math.tan(phi / 2);
        // --- clamp if the side is too short ---
        const dsClamped = Math.min(ds, len1 - 1e-3, len2 - 1e-3);
        // --- tangent points ---
        const start = {
            x: curr.x + (v1.x / len1) * dsClamped,
            y: curr.y + (v1.y / len1) * dsClamped
        };
        const end = {
            x: curr.x + (v2.x / len2) * dsClamped,
            y: curr.y + (v2.y / len2) * dsClamped
        };
        // --- first / next segments ---
        if (i === 0) {
            path.moveTo(start.x, start.y);
        }
        else {
            path.lineTo(start.x, start.y);
        }
        // --- direction of the arc (CW / CCW) ---
        const cross = v1.x * v2.y - v1.y * v2.x; // z-component
        const sweep = cross < 0; // for CW sweep (Canvas Y axis down)
        // --- always the short arc (largeArc = false) ---
        path.arcTo([end.x, end.y], cornerRadius, cornerRadius, /*largeArc*/ false, sweep);
    }
    path.close();
};
/**
 * Draws a polygon by connecting a series of points.
 * @param path - The path or multipath to draw on
 * @param points - Array of points defining the polygon vertices
 */
export const drawPolygon = (path, points) => {
    const n = points.length;
    for (let i = 0; i < n; i++) {
        const point = points[i];
        if (i === 0) {
            path.moveTo(point.x, point.y);
        }
        else {
            path.lineTo(point.x, point.y);
        }
    }
    path.close();
};
/**
 * Scales and positions a drawing element to fit within specified dimensions.
 * @param drawingElement - The path or multipath element to fit
 * @param width - Target width
 * @param height - Target height
 * @param strokeWidth - Stroke width to account for in sizing
 */
export const fitToSize = (drawingElement, width, height, strokeWidth) => {
    const bb = drawingElement.bbox();
    if (!bb.size.width || !bb.size.height) {
        return;
    }
    const targetW = width + strokeWidth;
    const targetH = height + strokeWidth;
    const sx = targetW / bb.size.width;
    const sy = targetH / bb.size.height;
    drawingElement.transform(new g.Transformation(new g.Matrix(sx, 0, 0, sy, -bb.origin.x * sx, -bb.origin.y * sy)));
};
/**
 * Inflates (expands outward) a polygon by moving each vertex along the external bisector.
 * @param points - Array of points defining the polygon vertices
 * @param r - Inflation radius value or array of radius values per vertex
 * @returns Array of inflated polygon points
 */
export function inflatePolygon(points, r) {
    const n = points.length;
    const out = [];
    for (let i = 0; i < n; i++) {
        const prev = points[(i + n - 1) % n];
        const curr = points[i];
        const next = points[(i + 1) % n];
        const radius = Array.isArray(r) ? r[i] : r;
        // --- vectors to the neighbors ---
        const v1 = { x: prev.x - curr.x, y: prev.y - curr.y };
        const v2 = { x: next.x - curr.x, y: next.y - curr.y };
        const len1 = Math.hypot(v1.x, v1.y);
        const len2 = Math.hypot(v2.x, v2.y);
        // --- normalized ---
        const n1 = { x: v1.x / len1, y: v1.y / len1 };
        const n2 = { x: v2.x / len2, y: v2.y / len2 };
        // --- internal angle φ (phi) ---
        const phi = Math.acos(Math.max(-1, Math.min(1, n1.x * n2.x + n1.y * n2.y)));
        // --- distance δ along the external bisector ---
        const delta = radius / Math.sin(phi / 2) - radius;
        // --- internal bisector ---
        let b = { x: n1.x + n2.x, y: n1.y + n2.y };
        const bLen = Math.hypot(b.x, b.y);
        b = { x: b.x / bLen, y: b.y / bLen };
        // --- external bisector = -internal bisector (for clockwise polygon) ---
        const outDir = { x: -b.x, y: -b.y };
        out.push({
            x: curr.x + outDir.x * delta,
            y: curr.y + outDir.y * delta
        });
    }
    return out;
}
